"use client";

import { useRef } from "react";
import Image from "next/image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

import { PageMentoringSchema } from "@/sanity/pages/PageMentoringTemplate";

import gridBGimg from "@/public/lp2/dear/grid.svg";
import blurImg from "@/public/lp2/dear/blur.svg";
import CheckMarkGreenIcon from "@/public/mentoring/check-mark-green.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function GettingStartSection({
  data,
}: {
  data: PageMentoringSchema;
}) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const content1Ref = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const dearTL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      dearTL.from(content1Ref.current.children, {
        y: "1rem",
        stagger: 0.1,
        delay: 0.25,
        scrollTrigger: {
          trigger: content1Ref.current,
          end: "center center",
          scrub: 1,
        },
      });
    },
    { scope: containerRef }
  );

  return (
    <section ref={containerRef} className="w-full">
      <div className="container-lp mx-auto pt-[12rem] lg:pt-[20rem] pb-[10rem]">
        <div className="flex flex-col items-start justify-center w-full">
          <div className="w-full max-w-[124rem] mx-auto">
            <div
              ref={content1Ref}
              className="mb-[4rem] lg:mb-[12rem] w-full relative"
            >
              {/* grid */}
              <div className="w-1/2 absolute left-1/2 top-[0%] -translate-x-1/2 -translate-y-1/2 z-[-1]">
                <Image
                  src={gridBGimg}
                  alt="grid background"
                  className="w-full"
                />
              </div>
              {/* shadow/blur */}
              <div className="w-1/2 absolute left-1/2 top-[0%] -translate-x-1/2 -translate-y-1/2 z-[-1]">
                <Image
                  src={blurImg}
                  alt="blur background"
                  className="dear-section-blur w-full"
                />
              </div>
              {/*  */}
              {data?.stepsTitle && (
                <PortableText
                  value={data?.stepsTitle ?? []}
                  components={customRichTextComponent({
                    block: {
                      h2: ({ children }) => (
                        <h2 className="gradient-title text-[4.5rem] lg:text-[7rem] px-[4rem] lg:px-0 font-[500] tracking-[-0.29rem] leading-[110%] w-full mb-[4rem] lg:mb-[6rem] text-center">
                          {children}
                        </h2>
                      ),
                    },
                  })}
                />
              )}
            </div>

            <div className="relative lg:px-[4rem]">
              {/*  */}
              <div className="absolute left-[1rem] lg:left-[8rem] top-0 h-full flex justify-center overflow-clip px-[.25rem]">
                <div className="absolute h-full w-[.25rem] rounded-full bg-white bg-opacity-10"></div>
                <div className="sticky top-[40vh] !w-[3.5rem] !h-[3.5rem] flex-shrink-0 rounded-full before:absolute before:h-[100vh] before:w-[.25rem] before:bottom-[1rem] before:rounded-full before:bg-[#4AC57F] before:-translate-x-1/2 before:left-1/2"></div>
              </div>
              {/*  */}

              {data?.stepsCards &&
                data?.stepsCards?.length &&
                data?.stepsCards?.map((card, index) => (
                  <div
                    key={index}
                    className="h-full flex gap-[2rem] lg:gap-[10rem]"
                  >
                    <div className="h-full flex items-center relative z-[2]">
                      <span className="min-w-[6rem] min-h-[6rem] w-[6rem] h-[6rem] lg:min-w-[12rem] lgmin-h-[12rem] lg:w-[12rem] lg:h-[12rem] rounded-full flex justify-center items-center text-[#00A876] border border-[#4AC57F] bg-black font-[600] text-[2.5rem] lg:text-[3.5rem]">
                        0{index + 1}
                      </span>
                    </div>
                    <div className="w-full flex flex-col gap-[1.5rem] pb-[4rem] lg:pb-[12rem]">
                      <h3 className="gradient-title sans text-[4rem] lg:text-[4.5rem] font-[500] tracking-[-0.29rem] leading-[110%] lg:leading-[129%] mb-0">
                        {card.title}
                      </h3>
                      <p className="sans text-white/80 text-[2.25rem] font-[400] leading-[140%]">
                        {card.text}
                      </p>

                      <ul className="">
                        {card?.list?.length &&
                          card?.list?.map((item, index) => (
                            <li
                              key={index + 100}
                              className="text-white/80 text-[2.25rem] font-[400] leading-[110%] w-full flex items-center gap-4 mb-[1.5rem]"
                            >
                              <Image
                                className="w-[3rem] h-[3rem] min-w-[3rem] min-h-[3rem]"
                                src={CheckMarkGreenIcon}
                                alt="check"
                              />
                              <span>{item}</span>
                            </li>
                          ))}
                      </ul>
                    </div>
                  </div>
                ))}

              {/*  */}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
